import { useMemo, useState } from "react";
import { Box, Button, Typography } from "../../../../imports/muiImport";
import {
    useDispatch,
    useNavigate,
    useSelector,
} from "../../../../imports/generalImport";

import { handleAmount } from "../../../../shared/extra";
import { actionNotifier } from "../../../../components/tostify";
import ModelLoading from "../../../../ModelLoading";
import { purchaseProduct } from "../../../../apiCalls/urls/urlFunction";
import { fetchUserProfile } from "../../../../store/userProfile/action";
import { useAuth } from "../../../../hooks/contexts/AuthContext";
import { v4 as uuidv4 } from "uuid";

/* ------------------------------------------------------
   MAIN COMPONENT
------------------------------------------------------ */

export default function UserPayButton() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();

    const DENOMINATIONS = useSelector(
        (state) => state.Denominations?.allData || []
    );
    const PRODUCT = useSelector((state) => state.ProductDetail?.allData || {});
    const PROFILE = useSelector((state) => state.UserProfile?.allData || {});
    const USER_BALANCE = useSelector((state) => state.UserBalance?.allData || 0);

    const [loading, setLoading] = useState({
        active: false,
        message: "Please wait...",
    });
    const [error, setError] = useState("");

    /* ------------------------------------------------------
        MATCHING PaymentOrderReceipt CALC LOGIC
    ------------------------------------------------------ */

    const filteredItems = useMemo(
        () => DENOMINATIONS.filter((item) => item?.QUANTITY >= 1),
        [DENOMINATIONS]
    );

    // TOTAL_VALUE = Σ (AMOUNT × QUANTITY)
    const TOTAL_VALUE = useMemo(() => {
        return filteredItems.reduce(
            (sum, item) => sum + (item.AMOUNT || 0) * (item.QUANTITY || 0),
            0
        );
    }, [filteredItems]);

    // TOTAL_DISCOUNT = Σ (AMOUNT – TOTAL)
    const TOTAL_DISCOUNT = useMemo(() => {
        return filteredItems.reduce(
            (sum, item) => sum + ((item.AMOUNT || 0) - (item.TOTAL || 0)),
            0
        );
    }, [filteredItems]);

    // NET_PAY = TOTAL_VALUE - TOTAL_DISCOUNT
    const NET_PAY = useMemo(() => {
        const baseNet = TOTAL_VALUE - TOTAL_DISCOUNT;
        return Math.max(baseNet - USER_BALANCE, 0);
    }, [TOTAL_VALUE, TOTAL_DISCOUNT, USER_BALANCE]);

    /* ------------------------------------------------------
       CREATE PAYMENT PAYLOAD
    ------------------------------------------------------ */
    const prepareDenominationPayload = (items) => {
        const denominations = items
            .filter((i) => i?.QUANTITY > 0)
            .map((i) => {
                let obj = {
                    denominationId: i?.thirdPartyId || null,
                    quantity: i?.QUANTITY || 0,
                }

                if (PRODUCT?.rangeDenomination) {
                    obj.denominationId = PRODUCT?.denominations?.[0]?.thirdPartyId;
                    obj.selectedAmount = i?.AMOUNT;
                }

                return obj;
            });

        return denominations || [];
    }

    const generatePayload = () => {
        const denominations = prepareDenominationPayload(filteredItems);
        const payload = {
            items: [
                {
                    "productId": PRODUCT?.publicId || null,
                    "denominations": denominations,
                },
            ],
            "referenceId": uuidv4(),
            "currency": PRODUCT?.currency?.code || 'USD'
        };
        return payload;
    };

    /* ------------------------------------------------------
       PAYMENT PROCESS
    ------------------------------------------------------ */
    const processPayment = async () => {
        setError("");
        if (loading.active) return;

        // No items selected
        if (!filteredItems.length) {
            actionNotifier.error("Please select denomination.");
            setError("Please select denomination.");
            return;
        }

        setLoading({
            active: true,
            message: "Processing your payment securely...",
        });

        try {
            const payload = generatePayload();
            const response = await purchaseProduct(payload);

            if (!response?.status) {
                throw new Error(response?.message || "Payment failed.");
            }

            actionNotifier.success(response?.message || "Payment failed. Try again.");
            dispatch(fetchUserProfile());
            navigate("/profile/order", { replace: true });
        } catch (err) {
            actionNotifier.error(err.message || "Payment failed. Try again.");
        } finally {
            setLoading({ active: false, message: "Please wait..." });
        }
    };

    /* ------------------------------------------------------
       MAIN BUTTON CLICK
    ------------------------------------------------------ */
    const handleClick = () => {
        if (!isLoggedIn) {
            navigate("/login", { replace: true });
            return;
        }

        processPayment();
    };

    /* ------------------------------------------------------
       UI
    ------------------------------------------------------ */
    return (
        <Box sx={{ mt: 2 }}>
            <ModelLoading loadingStatus={loading.active} message={loading.message} />

            <Button
                variant="contained"
                fullWidth
                disabled={false}
                onClick={handleClick}
                sx={{
                    minHeight: { xs: "55px", lg: "60px" },
                    fontSize: { xs: "1.2rem", lg: "1.4rem" },
                    fontWeight: 700,
                    borderRadius: 2,
                    color: "white",
                    "&:hover": { boxShadow: 5 },
                }}
            >
                {isLoggedIn
                    ? `Pay ₹ ${handleAmount(NET_PAY)}`
                    : `Login & Pay ₹ ${handleAmount(NET_PAY)}`}
            </Button>

            {error && (
                <Typography
                    color="error"
                    variant="subtitle2"
                    sx={{
                        mt: 1,
                        textAlign: "center",
                        textTransform: "capitalize",
                    }}
                >
                    {error}
                </Typography>
            )}
        </Box>
    );
}
